import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

export interface PredefinedVariableValue {
  id: string;
  value: string;
  predefinedVariableId: string;
}

interface PredefinedVariableValueSelectProps {
  predefinedVariableId: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

const PredefinedVariableValueSelect: React.FC<PredefinedVariableValueSelectProps> = ({
  predefinedVariableId,
  value,
  onChange,
  label = 'Value',
  disabled = false,
  size = 'small',
  required = false,
  error = false,
  helperText,
}) => {
  const dispatch = useAppDispatch();
  const [options, setOptions] = useState<PredefinedVariableValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newValueText, setNewValueText] = useState('');
  const [creating, setCreating] = useState(false);

  // Load values from API
  const loadValues = useCallback(async () => {
    if (!predefinedVariableId) return;

    setLoading(true);
    try {
      const response = await DataService.get(
        `api/Lov/predefined-variable/${predefinedVariableId}/values`
      );

      if (response.ok) {
        const data: PredefinedVariableValue[] = await response.json();
        setOptions(data);
      } else {
        throw new Error('Failed to load values');
      }
    } catch (error) {
      console.error('Failed to load predefined variable values:', error);
      dispatch(showAlert({ message: 'Failed to load value options', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [predefinedVariableId, dispatch]);

  useEffect(() => {
    loadValues();
  }, [loadValues]);

  const handleCreateValue = async () => {
    if (!newValueText.trim()) return;

    setCreating(true);
    try {
      const response = await DataService.post(
        `api/PredefinedValuesLookup/variable/${predefinedVariableId}/values`,
        { value: newValueText.trim() }
      );

      if (response.ok) {
        const result = await response.json();
        dispatch(showAlert({ message: 'Value created successfully', type: 'success' }));

        // Reload values to get the new one
        await loadValues();

        // Set the newly created value as selected
        onChange(newValueText.trim());

        // Close dialog and reset
        setOpenCreateDialog(false);
        setNewValueText('');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create value');
      }
    } catch (error: any) {
      console.error('Failed to create value:', error);
      dispatch(
        showAlert({
          message: error.message || 'Failed to create value',
          type: 'error',
        })
      );
    } finally {
      setCreating(false);
    }
  };

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: PredefinedVariableValue | string | null) => {
    if (typeof newValue === 'string') {
      // User typed a value that doesn't exist - open create dialog
      setNewValueText(newValue);
      setOpenCreateDialog(true);
    } else if (newValue) {
      onChange(newValue.value);
    } else {
      onChange('');
    }
  };

  const selectedOption = options.find(opt => opt.value === value) || null;

  return (
    <>
      <Autocomplete
        freeSolo
        options={options}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        loading={loading}
        disabled={disabled || !predefinedVariableId}
        size={size}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.value;
        }}
        isOptionEqualToValue={(option, value) => {
          if (typeof option === 'string' || typeof value === 'string') return false;
          return option.id === value.id;
        }}
        filterOptions={(options, params) => {
          const filtered = options.filter(option => {
            if (typeof option === 'string') return false;
            return option.value.toLowerCase().includes(params.inputValue.toLowerCase());
          });

          // Suggest creation if input doesn't match any option
          const inputValue = params.inputValue.trim();
          if (inputValue !== '' && !filtered.some(option => {
            if (typeof option === 'string') return false;
            return option.value === inputValue;
          })) {
            return [
              ...filtered,
              // This will be handled by freeSolo and onChange
            ];
          }

          return filtered;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          if (typeof option === 'string') return null;
          return (
            <Box component="li" {...props}>
              <Chip label={option.value} size="small" variant="outlined" />
            </Box>
          );
        }}
        noOptionsText={
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewValueText(inputValue);
                setOpenCreateDialog(true);
              }}
            >
              Create "{inputValue}"
            </Button>
          </Box>
        }
      />

      {/* Create Value Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => !creating && setOpenCreateDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create New Value</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Value"
            value={newValueText}
            onChange={(e) => setNewValueText(e.target.value)}
            disabled={creating}
            margin="dense"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCreateValue();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateValue}
            variant="contained"
            disabled={creating || !newValueText.trim()}
            startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PredefinedVariableValueSelect;
